import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as qs from 'query-string';
import _ from 'lodash';

import { useSelector } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';

import http from '@common/http';
import moment from 'moment';

import { Grid, Typography, Button as MuiButton, Dialog, DialogContent, DialogActions } from '@material-ui/core';

import CheckCircle from '@material-ui/icons/CheckCircle';

import { Button, Container, Header, ColivingDetail, GoogleMap, Footer } from '@common/components';

import { toast, useDetails, useSessionState, useTranslation, useI18n } from '@common/utils';

import { useSettings } from '@common/hooks';

import {
	Amenities,
	DatePickers,
	HeaderMessages,
	ProductImages,
	ProductOptions,
	RequestSubmitDialog,
} from '@components/Coliving';

import { Link } from 'react-router-dom';

const handleBookingError = (e) => {
	const tr = useTranslation();
	if (e?.data?.message === 'BookingOneOrMoreProductsUnavailable') {
		toast({ text: `${tr(e.data.message)}: ${e?.data?.extra?.processed || 0}`, type: 'error' });
	} else {
		toast({ text: e?.data?.message || e.message, type: 'error' });
	}
};

function ColivingDetails({ history, match }) {
	const classes = useStyles();
	const { id } = match.params;
	const { locale } = useSelector(state => state.locale);
	const params = qs.parse(history.location.search);
	const { currencySymbol, currencyCode } = useSettings();
	const [isRequestFormDialogOpen, setIsRequestFormDialogOpen] = useState(false);
	const [isRequestConfirmDialogOpen, setIsRequestConfirmDialogOpen] = useState(false);
	const [requestButton, setRequestButon] = useState({ text: 'Inquire', isSent: false });
	const [seconds, setSeconds] = useState(6);
	const tr = useTranslation();

	const { state: bookingId, setState: setBookingId } = useSessionState('booking-id', '');

	useEffect(async () => {
		if (!bookingId) {
			return;
		}

		const { data: booking } = await http.get(`/api/kiosks/coliving/bookings/${bookingId}`);

		if (booking.expired) {
			setBookingId('');
		}
	}, [bookingId]);

	const [quantity, setQuantity] = useState(1);

	const [state, setState] = useState({
		...params,
		from: params.from || null,
		to: params.to || null,
	});

	const { details: coliving, isLoading } = useDetails(`/api/kiosks/coliving/products/${id}?${qs.stringify(state)}`, {
		amenities: [],
		asset: '',
		assetCapacity: 0,
		assetPersonCapacity: 0,
		assetType: '',
		description: '',
		image: '',
		images: [],
		location: {
			coordinates: '',
			street: '',
			city: '',
			country: '',
			gettingThere: '',
		},
		options: [],
		perks: '',
		personCapacity: 0,
		pricePerDay: 0,
		pricePerDayAfterDiscount: 0,
		summary: '',
		title: '',
		isOnlyInquired: false,
		isPriceVisible: true,
		minDuration: '',
		maxDuration: '',
		i18n: {},
	}, [id, state]);

	const i18n = useI18n(coliving.i18n);
	const title = i18n('title', coliving.title);
	const description = i18n('productDescription', coliving.productDescription);
	const perks = i18n('perks', coliving.perks);

	useEffect(() => {
		history.replace({
			search: qs.stringify(state),
		});
	}, [state]);

	const handleBook = async () => {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({ ecommerce: null });
		window.dataLayer.push({ 'event': 'Buy Now' });
		window.dataLayer.push({
			event: 'begin_checkout',
			ecommerce: {
				currency: currencyCode,
				value: coliving.pricePerDayAfterDiscount || coliving.pricePerDay,
				items: [
					{
						item_id: coliving._id,
						item_name: coliving.title,
						discount: coliving.pricePerDayAfterDiscount ? coliving.pricePerDay - coliving.pricePerDayAfterDiscount : 0,
						price: coliving.total,
						quantity: coliving.durationInDays,
					},
				],
			},
		});

		const res = await http.get(`/api/kiosks/coliving/products/${id}?${qs.stringify(state)}`);

		if (!res.data.available) {
			toast({ text: tr('Booking is not available'), type: 'error' });
			return;
		}

		const products = new Array(quantity).fill({
			product: id,
			from: state.from,
			to: state.to,
		});

		if (bookingId) {
			http.post(`/api/kiosks/coliving/bookings/${bookingId}/products`, { products }).then(() => {
				history.push(`/booking/${bookingId}/?language=${locale}`);
			}).catch(handleBookingError);
		} else {
			http.post('/api/kiosks/coliving/bookings', { products }).then(({ data: bookings }) => {
				setBookingId(bookings._id);
				history.push(`/booking/${bookings._id}/?language=${locale}`);
			}).catch(handleBookingError);
		}
	};

	const handelRequestFormSubmit = async (e, values) => {
		e.preventDefault();

		const { name, surname, email, phone, companyName, companyCode, companyAddress, companyVATCode, agreeMarketingInfo } = values;

		let description = '';

		for (let [key, value] of Object.entries(_.omit(values, ['agreeMarketingInfo']))) {
			if (key && values) {
				if (key === 'companyName') {
					key = 'company name';
				}
				if (key === 'companyCode') {
					key = 'company code';
				}
				if (key === 'companyAddress') {
					key = 'company address';
				}
				if (key === 'companyVATCode') {
					key = 'company VAT code';
				}
				description = description.concat(`${key}: ${value}\n`);
			}
		}

		if (state.from && state.to) {
			description = `${description}${`from: ${moment(state.from).format('YYYY-MM-DD')}\n`}${`to: ${moment(state.to).format('YYYY-MM-DD')}\n`}`;
		}

		const updated = { name, surname, email, phone, companyName, companyCode, companyAddress, companyVATCode, agreeMarketingInfo, description: description, product: id, size: coliving?.total || 0, from: state.from, to: state.to };

		try {
			let interval;
			await http.post('/api/kiosks/coliving/products/opportunity', updated);

			toast({ text: tr('Request created') });

			setIsRequestFormDialogOpen(false);

			setRequestButon({ text: tr('Request sent'), isSent: true });

			setIsRequestConfirmDialogOpen(true);

			interval = setInterval(() => {
				setSeconds((prevSeconds) => prevSeconds - 1);
			}, 1000);

			setTimeout(() => {
				clearInterval(interval);
				window.history.back();
			}, 7000);

		} catch (e) {
			toast(e);
		}
	};

	const isDateSelected = state.from && state.to ? true : false;
	const isBookingAvailable = isDateSelected && coliving.available;
	const isPriceVisible = coliving?.isOnlyInquired && coliving?.isPriceVisible === false ? false : true;

	const BookOrInquiredButton = () => {
		if (coliving.isOnlyInquired) {
			return <Button wide={true} color="dark" onClick={() => setIsRequestFormDialogOpen(true)} disabled={!isDateSelected || requestButton.isSent}>{tr(requestButton.text)}</Button>;
		}
		if ((coliving.available || coliving.available === undefined)) {
			return <Button wide={true} color="dark" onClick={handleBook} disabled={!coliving.available}>{tr('Book Now')}</Button>;
		} else {
			return <Button wide={true} color="dark" onClick={() => setIsRequestFormDialogOpen(true)} disabled={!isDateSelected || requestButton.isSent}>{tr(requestButton.text)}</Button>;
		}
	};

	return (
		<div className={classes.root}>
			<HeaderMessages />
			<Header>
				<DatePickers dates={state} setDates={setState} boxes={true} productId={id} />
			</Header>

			<Container >
				<Grid container spacing={3} className={classes.padding}>
					<Grid item xs={12} style={{ marginTop: '-24px', marginBottom: '-12px', padding: 0 }}>
						<MuiButton className={classes.backButton} variant='text' component={Link} to={`/?${qs.stringify(state)}&language=${locale}`}>{`← ${tr('Back to search')}`}</MuiButton>
					</Grid>
					<Grid item xs={12} md={8}>
						<Typography variant="h2">
							{title}
						</Typography>
					</Grid>
					<Grid item xs={12} md={4}>
						{!coliving.pricePerDayAfterDiscount && isPriceVisible && <div className={classes.price}>
							<span className={classes.outline}>{coliving.pricePerDay} {currencySymbol}/{tr('night')}</span>
						</div>}
						{coliving.pricePerDayAfterDiscount > 0 && isPriceVisible && <div className={classes.price}>
							&nbsp;<span className={classes.outline} style={{ textDecorationLine: 'line-through', color: 'red' }}>{coliving.pricePerDay} {currencySymbol}</span>
							&nbsp;<span className={classes.outline}>{coliving.pricePerDayAfterDiscount} {currencySymbol}</span><span className={classes.outline}>/{tr('night')}</span>
						</div>}
					</Grid>
					<Grid item xs={12} md={8}>
						<ProductImages images={coliving.images} />

						<ColivingDetail header={tr('Description')} >
							<div dangerouslySetInnerHTML={{ __html: description ? description : '-' }} />
						</ColivingDetail>

						{coliving.amenities && coliving.amenities.length > 0 &&
							<ColivingDetail header={tr('Amenities')}>
								<Amenities amenities={coliving.amenities} large={true} />
							</ColivingDetail>
						}
						<ColivingDetail header={tr('Location')}>
							<>
								{coliving?.location?.street && <div>{coliving.location?.street}</div>}
								{coliving?.location?.city && <div>{coliving.location?.city}</div>}
								{coliving?.location?.country && <div>{coliving.location?.country}</div>}
							</>
						</ColivingDetail>

						{coliving?.location?.coordinates &&
							<div className={classes.map}>
								<GoogleMap coordinates={coliving.location?.coordinates} />
							</div>
						}

						{coliving?.location?.gettingThere &&
							<ColivingDetail header={tr('Getting here')}>
								<div dangerouslySetInnerHTML={{ __html: coliving.location?.gettingThere }} />
							</ColivingDetail>
						}

					</Grid>
					<Grid item xs={12} md={4}>
						{isPriceVisible &&
							<Typography variant="h3" style={{ marginBottom: '16px' }}>
								{tr(isBookingAvailable ? 'Refine your booking' : 'Add dates for prices')}
							</Typography>
						}


						<div className={`${perks !== '' ? '' : classes.options}`} dangerouslySetInnerHTML={{ __html: perks ? perks : '-' }} />

						<div className={classes.separator} />

						<div className={classes.options}>
							<ProductOptions coliving={coliving} search={qs.stringify(state)} quantity={quantity} setQuantity={setQuantity} />
						</div>
						{isPriceVisible &&
							<div className={`${classes.total} ${isBookingAvailable ? '' : classes.totalDisabled}`}>
								<div>{tr('Total')}</div>
								{isDateSelected && !isLoading &&
									<div>{coliving.pricePerDayAfterDiscount > 0 ? coliving.pricePerDayAfterDiscount * coliving.durationInDays : coliving.total * quantity} {currencySymbol}</div>
								}
							</div>
						}

						<div className={classes.dates}>
							{(coliving?.minDuration || coliving?.maxDuration) &&
								<Grid style={{ margin: '0 auto', paddingBottom: '12px' }} container spacing={2} item xs={12}>
									{coliving?.minDuration &&
										<Grid style={{ padding: '0 4px 0 0', alignItems: 'center', display: 'flex', flexDirection: 'column' }} item xs={12} sm={6}>
											<Typography variant="caption">
												{tr('Minimum duration')}
											</Typography>
											<Typography variant="subtitle2">
												{`${coliving?.minDuration} ${tr(coliving?.durationType === 'months' ? coliving?.durationType : 'nights')}`}
											</Typography>
										</Grid>
									}
									{coliving?.maxDuration &&
										<Grid style={{ padding: '0 0 0 4px', alignItems: 'center', display: 'flex', flexDirection: 'column' }} item xs={12} sm={6}>
											<Typography variant="caption">
												{tr('Maximum duration')}
											</Typography>
											<Typography variant="subtitle2">
												{`${coliving?.maxDuration} ${tr(coliving?.durationType === 'months' ? coliving?.durationType : 'nights')}`}
											</Typography>
										</Grid>
									}
								</Grid>
							}
							<DatePickers dates={state} setDates={setState} productId={id} minDuration={coliving?.minDuration || undefined} />
						</div>

						<BookOrInquiredButton />

						{!coliving.available && !isLoading && isDateSelected && !coliving.isOnlyInquired &&
							<Typography gutterBottom variant="body2" className={classes.error}>
								{coliving.available === false && coliving.isMinDurationValid === false &&
								`${tr('Minimum stay duration')} ${coliving?.minDuration} ${tr(coliving?.durationType === 'months' ? coliving?.durationType : 'nights')}`
								}
								{coliving.available === false && coliving.isMaxDurationValid === false &&
								`${tr('Maximum stay duration')} ${coliving?.maxDuration} ${tr(coliving?.durationType === 'months' ? coliving?.durationType : 'nights')}`
								}
								{coliving.available === false && coliving.isMaxDurationValid !== false && coliving.isMinDurationValid !== false &&
								`${tr('It is not available during selected dates')} ${moment(state.from).format('MMM DD')}-${moment(state.to).format('MMM DD')}`
								}
							</Typography>
						}
					</Grid>
				</Grid>
				<RequestSubmitDialog
					isOpen={isRequestFormDialogOpen}
					handleClose={() => setIsRequestFormDialogOpen(false)}
					handleAction={handelRequestFormSubmit}
					headline="Request for availability"
					question="Please fill contact form"
					actionLabel="Submit"
				/>
				<Dialog
					open={isRequestConfirmDialogOpen}
					onClose={() => setIsRequestConfirmDialogOpen(false)}
					fullWidth
					maxWidth={'sm'}>
					<DialogContent style={{ minHeight: '200px', display: 'flex', alignItems: 'center', color: '#2e7d32' }}>
						<CheckCircle style={{ fontSize: '36px', marginRight: '12px' }}/>
						<Typography variant="h5" style={{ marginBottom: '16px' }}>
							{tr('Thank you, your inquiry has been received and is being processed. Someone will contact you as soon as possible!')}
						</Typography>
					</DialogContent>
					<DialogActions style={{ fontSize: '14px', padding: '0 24px 24px 0' }}>
						{`${tr('Back to main page after')} ${seconds} ${tr('seconds')}`}
					</DialogActions>
				</Dialog >
			</Container>
			<Footer />
		</div>
	);
}

// AvailabilityChecker.propTypes = {
// 	state: PropTypes.object.isRequired,
// 	params: PropTypes.object.isRequired,
// 	setState: PropTypes.func.isRequired,
// 	handleCheckAvailability: PropTypes.func.isRequired,
// };

ColivingDetails.propTypes = {
	history: PropTypes.object,
	match: PropTypes.object,
};

export default ColivingDetails;

const useStyles = makeStyles(theme => ({
	root: {
		flexGrow: 1,
		position: 'relative',
		minHeight: '100vh',
	},
	section: {
		marginBottom: theme.spacing(5),
	},
	map: {
		height: '320px',
		marginTop: theme.spacing(-2),
		marginBottom: theme.spacing(5),
		background: theme.palette.custom.background,
	},
	error: {
		marginTop: theme.spacing(2),
		color: theme.palette.error.main,
	},

	price: {
		paddingTop: '10px',
		color: theme.palette.custom.main,
		fontSize: '20px',
		textAlign: 'right',
	},
	outline: {
		fontWeight: 600,
		fontSize: '28px',
	},

	options: {
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(2),
	},

	separator: {
		borderTop: `1px solid ${theme.palette.custom.separator}`,
	},

	dates: {
		marginBottom: theme.spacing(2),
	},

	total: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		fontWeight: 600,
		marginBottom: theme.spacing(2),
		padding: theme.spacing(2),
		background: theme.palette.custom.background,
	},

	totalDisabled: {
		opacity: 0.5,
	},
	padding: {
		paddingBottom: '100px',
	},
	backButton: {
		color: '#000091',
		'&:hover': {
			color: '#551A8B',
			backgroundColor: 'transparent',
		},
	},
}));

